import { interpretarNumeroPtBr, mensagemNumeroAmbiguo } from '@pieltec/contracts';
import { apiFetch, apiMutate } from './csrf-fetch';

export type AbaExpedicao = 'A_EXPEDIR' | 'PARCIAIS' | 'CONCLUIDAS';
export type SituacaoExpedicao = 'ABERTA' | 'CONCLUIDA' | '';

export interface FiltrosExpedicao {
  readonly aba: AbaExpedicao;
  readonly cliente: string;
  readonly pv: string;
  readonly situacao: SituacaoExpedicao;
  readonly pagina: number;
}

export interface DocumentoExpedicaoApi {
  readonly referencia: string;
  readonly tipo: string;
  readonly estado: 'CONFERIDO' | 'PENDENTE';
  readonly quantidadeCoberta: string;
}

export interface LinhaExpedicaoApi {
  readonly loteId: string;
  /** Código interno impresso na etiqueta (L-47); o UUID `loteId` é só identificador técnico. */
  readonly loteCodigo?: string;
  readonly reservado: string;
  readonly coletado: string;
  readonly restante: string;
  readonly localizacao?: { readonly nome: string; readonly emExpedicao: boolean };
  readonly politicaDocumental?: { readonly codigo: string; readonly versao: string; readonly estado: 'APROVADA' | 'PENDENTE' } | null;
  readonly documentos?: readonly DocumentoExpedicaoApi[];
}

export interface ExpedicaoResumoApi {
  readonly id: string;
  readonly retornoId: string;
  readonly reservado: string;
  readonly coletado: string;
  readonly cliente?: string;
  readonly pvNumero?: string;
  readonly previstaEm?: string;
}

export interface RomaneioExpedicaoApi {
  readonly numero: string;
  readonly confirmedAt: string;
  readonly volumes: number;
  readonly transportadoraOuRetirada: string;
}

export interface ExpedicaoDetalheApi {
  readonly expedicao: ExpedicaoResumoApi;
  /** L-48: texto do servidor quando não há política documental vigente neste ambiente. */
  readonly motivoBloqueio?: string | null;
  readonly linhas: readonly LinhaExpedicaoApi[];
  readonly coletas: readonly { readonly id: string; readonly confirmedAt: string; readonly romaneioNumero: string; readonly volumes: number; readonly transportadoraOuRetirada: string; readonly movimentoSaidaId?: string }[];
  readonly romaneios: readonly RomaneioExpedicaoApi[];
}

export interface ListaExpedicaoApi {
  readonly total: number;
  readonly items: readonly ExpedicaoResumoApi[];
}

export function filtrosExpedicaoVazios(): FiltrosExpedicao {
  return { aba: 'A_EXPEDIR', cliente: '', pv: '', situacao: '', pagina: 1 };
}

function abaDaUrl(valor: string | null): AbaExpedicao {
  if (valor === 'parciais') return 'PARCIAIS';
  if (valor === 'concluidas') return 'CONCLUIDAS';
  return 'A_EXPEDIR';
}

function situacaoDaUrl(valor: string | null): SituacaoExpedicao {
  return valor === 'ABERTA' || valor === 'CONCLUIDA' ? valor : '';
}

/** URL é a fonte de verdade do recorte; cliente/PV aguardam a projeção do servidor e nunca filtram saldo localmente. */
export function lerFiltrosExpedicao(search: string): FiltrosExpedicao {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const pagina = Number(params.get('page'));
  return {
    aba: abaDaUrl(params.get('aba')),
    cliente: params.get('cliente') ?? '',
    pv: params.get('pv') ?? '',
    situacao: situacaoDaUrl(params.get('situacao')),
    pagina: Number.isInteger(pagina) && pagina > 0 ? pagina : 1,
  };
}

export function escreverFiltrosExpedicao(filtros: FiltrosExpedicao): string {
  const params = new URLSearchParams();
  if (filtros.aba === 'PARCIAIS') params.set('aba', 'parciais');
  if (filtros.aba === 'CONCLUIDAS') params.set('aba', 'concluidas');
  if (filtros.cliente.trim()) params.set('cliente', filtros.cliente.trim());
  if (filtros.pv.trim()) params.set('pv', filtros.pv.trim());
  if (filtros.situacao) params.set('situacao', filtros.situacao);
  if (filtros.pagina > 1) params.set('page', String(filtros.pagina));
  return params.toString();
}

export function estadoDaAba(filtros: FiltrosExpedicao): SituacaoExpedicao {
  if (filtros.situacao) return filtros.situacao;
  if (filtros.aba === 'CONCLUIDAS') return 'CONCLUIDA';
  return 'ABERTA';
}

export function montarQueryExpedicao(filtros: FiltrosExpedicao): string {
  const params = new URLSearchParams();
  const estado = estadoDaAba(filtros);
  if (estado) params.set('estado', estado);
  params.set('page', String(filtros.pagina));
  params.set('limit', '50');
  return params.toString();
}

export type EstadoListaExpedicao = 'carregando' | 'erro' | 'vazio' | 'sem-resultado' | 'lista';

export function estadoDaListaExpedicao(entrada: { readonly carregando: boolean; readonly erro: boolean; readonly total: number; readonly comFiltro: boolean }): EstadoListaExpedicao {
  if (entrada.erro) return 'erro';
  if (entrada.carregando) return 'carregando';
  if (entrada.total === 0) return entrada.comFiltro ? 'sem-resultado' : 'vazio';
  return 'lista';
}

function numeroDecimal(valor: string): number {
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : 0;
}

/** Rótulo do lote para a tela: o código da etiqueta (L-47); sem ele, nunca o UUID inteiro no texto. */
export function rotuloLote(linha: Pick<LinhaExpedicaoApi, 'loteId' | 'loteCodigo'>): string {
  return linha.loteCodigo?.trim() || `(${linha.loteId.slice(0, 8)})`;
}

/**
 * A falta de qualquer evidência do contrato é bloqueio: a SPA nunca presume localização nem política. A política vem
 * decidida pelo servidor (L-48). Referência, tipo e cobertura documental são digitados no diálogo de saída — não são
 * pré-requisito para abri-lo — e o servidor continua sendo a autoridade.
 */
export function motivoBloqueioColeta(detalhe: ExpedicaoDetalheApi): string | null {
  if (detalhe.linhas.length === 0) return 'Não há lotes reservados para esta expedição.';
  if (detalhe.linhas.every((linha) => numeroDecimal(linha.restante) <= 0)) return 'Todos os lotes desta expedição já foram coletados.';
  for (const linha of detalhe.linhas) {
    if (numeroDecimal(linha.restante) <= 0) continue;
    const lote = rotuloLote(linha);
    if (!linha.localizacao) return `A localização atual do lote ${lote} não foi recebida pela API.`;
    if (!linha.localizacao.emExpedicao) return `O lote ${lote} está em ${linha.localizacao.nome}; transfira-o para Expedição antes da coleta.`;
    if (!linha.politicaDocumental || linha.politicaDocumental.estado !== 'APROVADA') {
      return detalhe.motivoBloqueio?.trim() || `A política documental do lote ${lote} está pendente ou ausente.`;
    }
  }
  return null;
}

export type LeituraQuantidadeColeta =
  | { readonly ok: true; readonly valor: number; readonly texto: string }
  | { readonly ok: false; readonly mensagem: string };

function emMilesimos(valor: number): number { return Math.round(valor * 10_000); }

/** Quantidade a coletar por lote: 0 < quantidade <= restante, até 4 casas, formato pt-BR sem adivinhar milhar. */
export function lerQuantidadeColeta(texto: string, restante: string): LeituraQuantidadeColeta {
  const lido = interpretarNumeroPtBr(texto);
  if (lido.tipo === 'vazio') return { ok: false, mensagem: 'Informe a quantidade a coletar.' };
  if (lido.tipo === 'invalido') return { ok: false, mensagem: 'Informe uma quantidade válida.' };
  if (lido.tipo === 'ambiguo') return { ok: false, mensagem: mensagemNumeroAmbiguo(texto, lido) };
  if (Math.abs(lido.valor * 10_000 - emMilesimos(lido.valor)) > 1e-6) return { ok: false, mensagem: 'Use no máximo 4 casas decimais.' };
  if (lido.valor <= 0) return { ok: false, mensagem: 'A quantidade a coletar deve ser maior que zero.' };
  if (emMilesimos(lido.valor) > emMilesimos(numeroDecimal(restante))) {
    return { ok: false, mensagem: `A quantidade não pode passar do que resta coletar (${String(numeroDecimal(restante)).replace('.', ',')}).` };
  }
  return { ok: true, valor: lido.valor, texto: lido.valor.toFixed(4) };
}

export interface EntradaColetaLote {
  readonly quantidade: string;
  readonly referencia: string;
  readonly tipo: string;
  /** Nulo = acompanha a quantidade (cobertura padrão). */
  readonly cobertura: string | null;
}

export interface LinhaColetaEnvio {
  readonly loteId: string;
  readonly quantidade: string;
  readonly documentos: readonly { readonly referencia: string; readonly tipo: string; readonly quantidadeCoberta: string; readonly estado: 'CONFERIDO' }[];
}

/** Linhas do POST de coleta com a quantidade informada pelo usuário (L-49); recusa antes de enviar se algo estiver inválido. */
export function montarLinhasColeta(
  linhas: readonly LinhaExpedicaoApi[],
  entradas: Readonly<Record<string, EntradaColetaLote>>,
): { readonly linhas: readonly LinhaColetaEnvio[] } | { readonly erro: string } {
  const envio: LinhaColetaEnvio[] = [];
  for (const linha of linhas) {
    if (numeroDecimal(linha.restante) <= 0) continue;
    const entrada = entradas[linha.loteId];
    const lote = rotuloLote(linha);
    if (!entrada) return { erro: `Informe a quantidade do lote ${lote}.` };
    const quantidade = lerQuantidadeColeta(entrada.quantidade, linha.restante);
    if (!quantidade.ok) return { erro: `Lote ${lote}: ${quantidade.mensagem}` };
    if (!entrada.referencia.trim() || !entrada.tipo.trim()) return { erro: `Informe referência e tipo da documentação conferida do lote ${lote}.` };
    let coberta = quantidade.texto;
    if (entrada.cobertura !== null) {
      const lida = interpretarNumeroPtBr(entrada.cobertura);
      if (lida.tipo !== 'ok' || emMilesimos(lida.valor) < emMilesimos(quantidade.valor)) {
        return { erro: `Lote ${lote}: a cobertura documental precisa cobrir a quantidade a coletar.` };
      }
      coberta = lida.valor.toFixed(4);
    }
    envio.push({ loteId: linha.loteId, quantidade: quantidade.texto, documentos: [{ referencia: entrada.referencia.trim(), tipo: entrada.tipo.trim(), quantidadeCoberta: coberta, estado: 'CONFERIDO' }] });
  }
  if (envio.length === 0) return { erro: 'Não há lote com saldo a coletar.' };
  return { linhas: envio };
}

export async function listarExpedicoes(filtros: FiltrosExpedicao): Promise<ListaExpedicaoApi> {
  return apiFetch<ListaExpedicaoApi>(`/api/v1/expedicoes?${montarQueryExpedicao(filtros)}`);
}

export async function detalharExpedicao(retornoId: string): Promise<ExpedicaoDetalheApi> {
  const resposta = await apiFetch<Omit<ExpedicaoDetalheApi, 'romaneios'> & { readonly romaneios?: readonly RomaneioExpedicaoApi[] }>(`/api/v1/expedicoes/${encodeURIComponent(retornoId)}`);
  return {
    ...resposta,
    romaneios: resposta.romaneios ?? resposta.coletas.map((coleta) => ({ numero: coleta.romaneioNumero, confirmedAt: coleta.confirmedAt, volumes: coleta.volumes, transportadoraOuRetirada: coleta.transportadoraOuRetirada })),
  };
}

export async function confirmarColeta(retornoId: string, input: unknown): Promise<Response> {
  return apiMutate(`/api/v1/expedicoes/${encodeURIComponent(retornoId)}/coletas`, { method: 'POST', body: input });
}

/** Solicitação de correção (estorno) de saída física, como a API lista (L-56). */
export interface SolicitacaoEstornoApi {
  readonly id: string;
  readonly coletaId: string;
  readonly retornoId: string;
  readonly pvNumero: string | null;
  readonly loteCodigo: string | null;
  readonly quantidade: string;
  readonly solicitanteUserId: string;
  readonly solicitanteNome: string | null;
  readonly justificativaSolicitacao: string;
  readonly solicitadaEm: string;
  readonly estado: 'PENDENTE_REVISAO' | 'EXECUTADA';
  readonly versao: number;
  readonly executorUserId: string | null;
}

export const TEXTO_SEGREGACAO_ESTORNO = 'Quem solicitou não executa (segregação de funções).';

export async function listarSolicitacoesEstorno(): Promise<readonly SolicitacaoEstornoApi[]> {
  return apiFetch<readonly SolicitacaoEstornoApi[]>('/api/v1/expedicoes/estornos/solicitacoes');
}

/** Estado do botão "Executar estorno" de uma solicitação: escondido sem a permissão, desabilitado para o próprio solicitante. */
export function estadoDoBotaoExecutarEstorno(
  solicitacao: Pick<SolicitacaoEstornoApi, 'estado' | 'solicitanteUserId'>,
  sessao: { readonly userId: string | null; readonly permissoes: readonly string[] },
): { readonly visivel: boolean; readonly habilitado: boolean; readonly motivo: string | null } {
  if (solicitacao.estado !== 'PENDENTE_REVISAO' || !sessao.permissoes.includes('expedicao:executar-estorno')) return { visivel: false, habilitado: false, motivo: null };
  if (sessao.userId !== null && solicitacao.solicitanteUserId === sessao.userId) return { visivel: true, habilitado: false, motivo: TEXTO_SEGREGACAO_ESTORNO };
  return { visivel: true, habilitado: true, motivo: null };
}

/** Executa o estorno com a sessão de quem executa; a API devolve a mensagem pt-BR no erro (segregação, versão, confirmação). */
export async function executarEstornoExpedicao(
  solicitacao: Pick<SolicitacaoEstornoApi, 'id' | 'versao'>,
  entrada: { readonly justificativa: string; readonly materialPermaneceLocal: boolean },
): Promise<{ readonly ok: true } | { readonly ok: false; readonly status: number; readonly mensagem: string }> {
  const resposta = await apiMutate(`/api/v1/expedicoes/estornos/solicitacoes/${encodeURIComponent(solicitacao.id)}/executar`, {
    method: 'POST',
    body: { versao: solicitacao.versao, justificativa: entrada.justificativa.trim(), materialPermaneceLocal: entrada.materialPermaneceLocal },
  });
  if (resposta.ok) return { ok: true };
  const corpo = await resposta.json().catch(() => null) as { readonly message?: string } | null;
  const mensagem = resposta.status === 409
    ? `${corpo?.message ?? 'Conflito de revisão.'} Recarregue a lista antes de tentar de novo.`
    : corpo?.message ?? 'Não foi possível executar o estorno.';
  return { ok: false, status: resposta.status, mensagem };
}
